/**
 * bb-coupons-page.js — Bara Bros Coupon Vault Page (Phase 15G)
 * Handles campaigns, claim, wallet read, daily check-in, and tab UI.
 */
(function () {
  'use strict';

  // ── Module state ─────────────────────────────────────────────────────────────
  var BCP_REDEEM_URL = '/apps/bb-wishlist/points/redeem';
  var root, urls;

  var cfg = {};
  var reminderEl = null;
  var countdownTimerInterval = null;
  var _pageDataCtrl = null;

  // ── SessionStorage campaign cache ─────────────────────────────────────────────
  var CAMPAIGNS_CACHE_KEY = 'bb_coupon_campaigns_cache_v1';
  var CAMPAIGNS_CACHE_TTL = 5 * 60 * 1000;

  var state = {
    campaigns:     [],
    wallet:        null,
    walletGroups:  {},
    walletCounts:  { all: 0, unused: 0, used: 0, expired: 0, expiring_soon: 0, new: 0 },
    activeTab:     'available',
    activeFilter:  'all',
    claimedIds:    new Set(),
    claimedCodes:  new Set(),
    loggedIn:      null,
    checkinState:  null,
    nextMilestone: null,
    vipState:      null,
    referralState:      null,
    pointsState:        null,
    personalizedRewards: null,
    rewardsActivity:    null,
    missionState:       null,
    pointsHistory:        null,
    pointsHistoryFilter:  'all',
    pointsHistoryLoading: false
  };

  // ── Element refs ──────────────────────────────────────────────────────────────
  var el = {};

  function cacheEls() {
    el.checkinCard   = document.getElementById('bcp-checkin-card');
    el.checkinBtn    = document.getElementById('bcp-checkin-btn');
    el.checkinStatus = document.getElementById('bcp-checkin-status');
    // Tab mode
    el.heroStats     = document.getElementById('bcp-hero-stats');
    el.tabBar        = document.getElementById('bcp-tab-bar');
    el.sectionHead   = document.getElementById('bcp-section-head');
    el.mainGrid      = document.getElementById('bcp-main-grid');
    // Legacy mode
    el.campaignGrid  = document.getElementById('bcp-campaigns-grid');
    el.collectAll    = document.getElementById('bcp-collect-all');
    el.filterChips   = document.getElementById('bcp-filter-chips');
    el.walletGrid    = document.getElementById('bcp-wallet-grid');
    // VIP
    el.vipBadge      = document.getElementById('bcp-vip-badge');
    // Referral
    el.referralCta   = document.getElementById('bcp-referral-cta');
    // Points
    el.pointsCard        = document.getElementById('bcp-points-card');
    // Personalized
    el.personalizedCard  = document.getElementById('bcp-personalized-card');
  }

  // ── Init ──────────────────────────────────────────────────────────────────────
  function init() {
    root = document.querySelector('[data-bcp-root]');
    if (!root) return;

    urls = {
      campaigns:     root.dataset.campaignsUrl,
      claim:         root.dataset.claimUrl,
      wallet:        root.dataset.walletUrl,
      checkin:       root.dataset.checkinUrl,
      pageData:      root.dataset.campaignsUrl
        ? root.dataset.campaignsUrl.replace(/\/campaigns\/?$/, '/page-data')
        : null,
      pointsHistory: root.dataset.campaignsUrl
        ? root.dataset.campaignsUrl.replace(/\/coupons\/campaigns\/?$/, '/points/history')
        : null
    };

    cfg = {
      countdownEnabled:     root.dataset.countdownEnabled     !== 'false',
      reminderEnabled:      root.dataset.reminderEnabled      !== 'false',
      expiringSoonHours:    parseInt(root.dataset.expiringSoonHours       || '48',  10) || 48,
      reminderDismissHours: parseInt(root.dataset.reminderDismissHours    || '12',  10) || 12,
      countdownRefreshSecs: parseInt(root.dataset.countdownRefreshSeconds || '60',  10) || 60,
      tabsEnabled:          root.dataset.enableTabs       !== 'false',
      heroStatsEnabled:     root.dataset.enableHeroStats  !== 'false',
      motionEnabled:        root.dataset.motionEnabled    !== 'false',
      loginCtaText:         root.dataset.loginCta         || 'Log in to access your Coupon Vault',
      availableEmptyText:   root.dataset.availableEmpty   || 'No active coupons right now. Check back soon or earn rewards through daily check-in.',
      walletEmptyText:      root.dataset.emptyWalletText  || 'Your wallet is empty. Collect coupons above or check in daily.',
      expiringEmptyText:    root.dataset.expiringEmpty    || 'No coupons expiring soon — you\'re all good!',
      usedEmptyText:        root.dataset.usedEmpty        || 'No used or expired coupons yet.',
      vipShowPerkGrid:      root.dataset.vipShowPerkGrid       !== 'false',
      vipShowRequirements:  root.dataset.vipShowRequirements   !== 'false',
      vipExpiryLabel:       root.dataset.vipExpiryLabel        || 'Valid through Dec 31, {year}',
      pointsShowExpiring:   root.dataset.pointsShowExpiring    !== 'false',
      pointsShowHistory:    root.dataset.pointsShowHistory     !== 'false',
      pointsHistoryLimit:   parseInt(root.dataset.pointsHistoryLimit || '20', 10) || 20
    };

    cacheEls();
    injectToast();
    injectReminderComponent();

    if (el.checkinBtn) {
      el.checkinBtn.addEventListener('click', handleCheckin);
    }

    loadInitial();
  }

  // ── Data loading ──────────────────────────────────────────────────────────────
  function loadInitial() {
    var cached = getCachedCampaigns();
    if (cached && cached.length > 0) {
      state.campaigns = cached;
      if (cfg.tabsEnabled && el.mainGrid && state.activeTab === 'available') renderAvailableTab();
      else if (!cfg.tabsEnabled && el.campaignGrid) renderCampaignGrid();
    }

    if (urls.pageData) {
      fetchPageData();
    } else {
      loadFallback();
    }
  }

  function fetchPageData() {
    if (_pageDataCtrl) { try { _pageDataCtrl.abort(); } catch (e) {} }
    _pageDataCtrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var signal    = _pageDataCtrl ? _pageDataCtrl.signal : undefined;
    var timeoutId = _pageDataCtrl ? setTimeout(function () {
      if (_pageDataCtrl) _pageDataCtrl.abort();
    }, 8000) : null;

    var fetchOpts = signal ? { signal: signal } : {};

    apiFetch(urls.pageData, fetchOpts)
      .then(function (d) {
        clearTimeout(timeoutId);
        _pageDataCtrl = null;
        if (!d || !d.ok) throw new Error((d && d.error) ? d.error : 'page-data error');

        if (window.location.search.indexOf('debugRewards=1') !== -1) {
          console.log('[BB Coupons][debugRewards] ── page-data ──');
          console.log('[BB Coupons][debugRewards] endpoint:', urls.pageData);
          console.log('[BB Coupons][debugRewards] ok:', d.ok, '| logged_in:', d.logged_in, '| customer_id_present:', !!d.logged_in);
          console.log('[BB Coupons][debugRewards] campaigns:', d.campaigns ? d.campaigns.length : 0, '| wallet_coupons:', d.wallet && d.wallet.coupons ? d.wallet.coupons.length : 0);
          console.log('[BB Coupons][debugRewards] claim_url:', urls.claim);
          console.log('[BB Coupons][debugRewards] checkin_state:', d.checkin_state ? 'present' : 'null', '| points_state:', d.points_state ? 'present' : 'null');
        }

        state.loggedIn     = d.logged_in;
        state.campaigns    = dedupeCampaigns(d.campaigns);
        state.wallet       = d.wallet       || null;
        state.walletGroups = d.groups       || {};
        state.walletCounts = d.counts       || { all: 0, unused: 0, used: 0, expired: 0, expiring_soon: 0, new: 0 };
        state.checkinState  = d.checkin_state  || null;
        state.nextMilestone = d.next_milestone || null;
        state.vipState      = d.vip_state      || null;
        state.referralState       = d.referral_state       || null;
        state.pointsState         = d.points_state         || null;
        state.personalizedRewards = d.personalized_rewards || null;
        state.rewardsActivity     = d.rewards_activity     || null;
        state.missionState        = d.mission_state        || null;
        buildClaimedSets(d.wallet && d.wallet.coupons ? d.wallet.coupons : []);
        setCachedCampaigns(d.campaigns || []);
        renderAll();
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        _pageDataCtrl = null;
        var isAbort = err && (err.name === 'AbortError' || (err.message && err.message.indexOf('aborted') !== -1));
        if (isAbort) {
          console.error('[BB Coupons] page-data timeout after 8s');
          showLoadError('Coupon Center could not load. Please refresh.');
        } else {
          console.error('[BB Coupons] page-data failed — falling back to separate endpoints:', err);
          loadFallback();
        }
      });
  }

  function loadFallback() {
    var pending = 2;
    function onDone() {
      pending--;
      if (pending === 0) renderAll();
    }

    apiFetch(urls.campaigns)
      .then(function (d) {
        if (d.login_required) {
          state.loggedIn = false;
        } else if (d.ok) {
          state.campaigns = d.campaigns || [];
          if (state.loggedIn === null) state.loggedIn = !!d.logged_in;
        }
      })
      .catch(function (e) { console.error('[BB Coupons] Campaigns fallback error:', e); })
      .then(onDone);

    apiFetch(urls.wallet)
      .then(function (d) {
        if (d.login_required) {
          state.loggedIn = false;
        } else if (d.ok) {
          state.loggedIn     = true;
          state.wallet       = d.wallet       || null;
          state.walletGroups = d.groups       || {};
          state.walletCounts = d.counts       || { all: 0, unused: 0, used: 0, expired: 0, expiring_soon: 0, new: 0 };
          buildClaimedSets(d.wallet && d.wallet.coupons ? d.wallet.coupons : []);
        }
      })
      .catch(function (e) { console.error('[BB Coupons] Wallet fallback error:', e); })
      .then(onDone);
  }

  function refreshWallet() {
    return apiFetch(urls.wallet)
      .then(function (d) {
        if (!d.ok) return;
        state.wallet       = d.wallet       || null;
        state.walletGroups = d.groups       || {};
        state.walletCounts = d.counts       || { all: 0, unused: 0, used: 0, expired: 0, expiring_soon: 0, new: 0 };
        buildClaimedSets(d.wallet && d.wallet.coupons ? d.wallet.coupons : []);
        if (cfg.tabsEnabled) {
          renderHeroStats();
          renderTabBar();
          renderTabContent();
        } else {
          renderFilterChips();
          renderWalletGrid();
          renderCampaignGrid();
        }
        updateReminderComponent();
        /* Signal account page widget (same tab via event, other tabs via storage) */
        try {
          localStorage.setItem('bb_wallet_updated_at', String(Date.now()));
          document.dispatchEvent(new CustomEvent('bb:wallet:changed', { detail: { counts: state.walletCounts } }));
        } catch(e) {}
      })
      .catch(function (e) { console.error('[BB Coupons] Wallet refresh error:', e); });
  }

  // ── Referral CTA (coupon center) ─────────────────────────────────────────────
  function renderReferralCta() {
    if (!el.referralCta) return;
    var ref = state.referralState;
    if (!ref || !state.loggedIn) { el.referralCta.style.display = 'none'; return; }
    var link = ref.referral_link || '';
    el.referralCta.innerHTML =
      '<div class="bcp-referral-cta">' +
        '<div class="bcp-referral-cta__text">' +
          '<span class="bcp-referral-cta__label">Share Bara Bros and unlock future rewards.</span>' +
          '<span class="bcp-referral-cta__code">' + esc(ref.referral_code || '') + '</span>' +
        '</div>' +
        '<button class="bcp-referral-cta__btn" type="button" data-bcp-copy-ref="' + esc(link) + '">Copy Referral Link</button>' +
      '</div>';
    el.referralCta.querySelectorAll('[data-bcp-copy-ref]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var orig = btn.textContent;
        var val  = btn.getAttribute('data-bcp-copy-ref');
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(val).then(function() {
            btn.textContent = 'Copied!';
            setTimeout(function() { btn.textContent = orig; }, 2000);
          }).catch(function() {});
        }
      });
    });
    el.referralCta.style.display = '';
  }

  // ── VIP tier badge ────────────────────────────────────────────────────────────
  function renderVipBadge() {
    if (!el.vipBadge) return;
    var vip = state.vipState;
    if (!vip || !state.loggedIn) {
      el.vipBadge.style.display = 'none';
      return;
    }
    var progressHtml = '';
    if (vip.next_tier_label && vip.points_to_next_tier > 0) {
      var pct = vip.progress_percent || 0;
      progressHtml =
        '<div class="bcp-vip-progress">' +
          '<div class="bcp-vip-progress-track"><div class="bcp-vip-progress-fill" style="width:' + pct + '%"></div></div>' +
          '<span class="bcp-vip-progress-label">' +
            esc(String(vip.points_to_next_tier)) + ' more to reach ' + esc(vip.next_tier_label) +
          '</span>' +
        '</div>';
    }
    el.vipBadge.innerHTML =
      '<div class="bcp-vip-badge">' +
        '<div class="bcp-vip-badge__left">' +
          '<span class="bcp-vip-badge__tier">' + esc(vip.tier_label) + '</span>' +
          '<span class="bcp-vip-badge__score">' + esc(String(vip.score)) + ' VIP score</span>' +
        '</div>' +
        progressHtml +
        '<div class="bcp-vip-badge__note">Keep checking in and using coupons to level up.</div>' +
      '</div>';
    el.vipBadge.style.display = '';
  }

  // ── Bara Points redemption card (coupon vault) ───────────────────────────────
  function renderBcpPointsCard() {
    if (!el.pointsCard) return;
    var pts = state.pointsState;
    if (!pts || !state.loggedIn) { el.pointsCard.style.display = 'none'; return; }

    var available = pts.available_points || 0;
    var pending   = pts.pending_points   || 0;
    var tiersHtml = '';

    if (pts.redemption_enabled) {
      var tiers = [
        { tier: 'PTS_5',  label: '$5 off',  pts: 100, min: '$30'  },
        { tier: 'PTS_10', label: '$10 off', pts: 200, min: '$50'  },
        { tier: 'PTS_25', label: '$25 off', pts: 400, min: '$100' }
      ];
      tiersHtml = '<div class="bb-ach__points-tiers">' +
        tiers.map(function(t) {
          var canAfford = available >= t.pts;
          return '<div class="bb-ach__points-tier">' +
            '<div class="bb-ach__points-tier-info">' +
              '<span class="bb-ach__points-tier-label">' + t.label + '</span>' +
              '<span class="bb-ach__points-tier-cost">' + t.pts + ' pts</span>' +
              '<span class="bb-ach__points-tier-min">Min order ' + t.min + '</span>' +
            '</div>' +
            '<button type="button" class="bb-ach__points-redeem-btn' + (canAfford ? '' : ' is-disabled') + '"' +
              ' data-bcp-points-redeem="' + t.tier + '"' +
              (canAfford ? '' : ' disabled aria-disabled="true"') + '>' +
              (canAfford ? 'Redeem' : 'Need ' + t.pts + ' pts') +
            '</button>' +
          '</div>';
        }).join('') +
      '</div>';
    } else if (pts.redemption_test_mode) {
      tiersHtml = '<p class="bb-ach__points-note">Points redemption is in early testing. Keep earning — it\'s coming soon!</p>';
    } else {
      tiersHtml = '<p class="bb-ach__points-note">Earn points from check-ins and qualifying orders. Redemption coming soon.</p>';
    }

    el.pointsCard.innerHTML =
      '<div class="bcp-glass" style="padding:16px 18px;margin-bottom:8px">' +
        '<div class="bb-ach__section-header"><h3 class="bb-ach__section-title">Bara Points</h3></div>' +
        '<div class="bb-ach__points-stats">' +
          '<div class="bb-ach__points-stat"><span class="bb-ach__points-value">' + available + '</span><span class="bb-ach__points-label">Available</span></div>' +
          '<div class="bb-ach__points-stat"><span class="bb-ach__points-value">' + pending + '</span><span class="bb-ach__points-label">Pending</span></div>' +
        '</div>' +
        tiersHtml +
      '</div>';

    el.pointsCard.querySelectorAll('[data-bcp-points-redeem]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        if (btn.disabled || btn.classList.contains('is-disabled') || btn.dataset.bcpRedeemPending) return;
        var tier = btn.getAttribute('data-bcp-points-redeem');
        btn.dataset.bcpRedeemPending = '1';
        var orig = btn.textContent;
        btn.textContent = 'Redeeming…';
        btn.disabled = true;
        fetch(BCP_REDEEM_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tier: tier })
        })
        .then(function(r) { return r.json(); })
        .then(function(data) {
          if (data.ok) {
            btn.textContent = 'Saved ✓';
            btn.classList.add('is-redeemed');
          } else {
            btn.textContent = orig;
            btn.disabled = false;
            delete btn.dataset.bcpRedeemPending;
          }
        })
        .catch(function() {
          btn.textContent = orig;
          btn.disabled = false;
          delete btn.dataset.bcpRedeemPending;
        });
      });
    });

    el.pointsCard.style.display = '';
  }

  // ── "Best For You" personalized banner (coupon vault top) ────────────────────
  function renderBcpPersonalizedCard() {
    if (!el.personalizedCard) return;
    var pr = state.personalizedRewards;
    if (!pr || !pr.enabled || !pr.primary_message || !state.loggedIn) {
      el.personalizedCard.style.display = 'none';
      return;
    }

    var nudgePills = (Array.isArray(pr.nudges) ? pr.nudges : []).slice(0, 3).map(function (n) {
      return '<span class="bcp-personalized__pill">' + esc(n.label || '') + '</span>';
    }).join('');

    el.personalizedCard.innerHTML =
      '<div class="bcp-personalized-card bcp-glass">' +
        (nudgePills ? '<div class="bcp-personalized__pills">' + nudgePills + '</div>' : '') +
        '<p class="bcp-personalized__msg">' + esc(pr.primary_message) + '</p>' +
        (pr.secondary_message
          ? '<p class="bcp-personalized__sub">' + esc(pr.secondary_message) + '</p>'
          : '') +
      '</div>';
    el.personalizedCard.style.display = '';
  }

  // ── Render orchestration ───────────────────────────────────────────────────────
  function renderAll() {
    renderCheckinCard();
    applyCheckinState(state.checkinState);
    renderHeroStats();
    renderVipBadge();
    renderBcpPersonalizedCard();
    renderBcpPointsCard();
    renderReferralCta();
    if (cfg.tabsEnabled) {
      renderTabBar();
      renderTabContent();
    } else {
      renderCampaignGrid();
      renderFilterChips();
      renderWalletGrid();
    }
    startCountdownTimer();
    updateReminderComponent();
    notifyExpiringOnce();
  }

  // ── Hero stats ────────────────────────────────────────────────────────────────
  function renderHeroStats() {
    if (!el.heroStats || !cfg.heroStatsEnabled) return;

    if (state.loggedIn === null) {
      el.heroStats.className = '';
      el.heroStats.innerHTML = '';
      return;
    }

    if (state.loggedIn === false) {
      el.heroStats.className = 'bcp-hero-login';
      el.heroStats.innerHTML =
        '<p class="bcp-hero__note">' + esc(cfg.loginCtaText) + '</p>' +
        '<a href="' + esc(loginUrl()) + '" class="bcp-btn bcp-btn--primary bcp-btn--sm">Log In</a>';
      return;
    }

    var uncollected = state.campaigns.filter(function (c) {
      return !state.claimedIds.has(String(c.id));
    }).length;
    var streak = state.checkinState ? (state.checkinState.streak || 0) : 0;

    var stats = [
      { value: uncollected,                            label: 'Available' },
      { value: state.walletCounts.unused    || 0,      label: 'Saved' },
      { value: state.walletCounts.expiring_soon || 0,  label: 'Expiring' },
      { value: streak,                                 label: 'Day Streak' }
    ];

    el.heroStats.className = 'bcp-hero-stats';
    el.heroStats.innerHTML = stats.map(function (s) {
      return '<div class="bcp-stat-pill">' +
        '<span class="bcp-stat-pill__value">' + s.value + '</span>' +
        '<span class="bcp-stat-pill__label">' + esc(s.label) + '</span>' +
        '</div>';
    }).join('');
  }

  // ── Tab bar ────────────────────────────────────────────────────────────────────
  var TABS = [
    { key: 'available', label: 'Available' },
    { key: 'wallet',    label: 'My Wallet' },
    { key: 'expiring',  label: 'Expiring Soon' },
    { key: 'used',      label: 'Used' },
    { key: 'all',       label: 'All' },
    { key: 'points',    label: 'Points' },
    { key: 'vip',       label: 'VIP' },
    { key: 'referrals', label: 'Referrals' },
    { key: 'reviews',   label: 'Reviews' },
    { key: 'activity',  label: 'Activity' },
    { key: 'missions',  label: 'Missions' }
  ];

  function getTabCounts() {
    var uncollected = state.campaigns.filter(function (c) {
      return !state.claimedIds.has(String(c.id));
    }).length;
    var pts = state.pointsState;
    var ref = state.referralState;
    return {
      available: uncollected,
      wallet:    state.walletCounts.unused        || 0,
      expiring:  state.walletCounts.expiring_soon || 0,
      used:      (state.walletCounts.used   || 0) + (state.walletCounts.expired || 0),
      all:       state.walletCounts.all           || 0,
      points:    pts ? (pts.available_points || 0) : 0,
      vip:       0,
      referrals: ref ? (ref.rewards_pending || 0) : 0,
      reviews:   0,
      activity:  state.rewardsActivity ? (state.rewardsActivity.count || 0) : 0,
      missions:  state.missionState && state.missionState.summary ? (state.missionState.summary.claimable_count || 0) : 0
    };
  }

  function renderTabBar() {
    if (!el.tabBar) return;
    var counts = getTabCounts();

    el.tabBar.innerHTML = TABS.map(function (t) {
      var isActive  = state.activeTab === t.key;
      var count     = counts[t.key];
      var countHtml = (count > 0 || t.key === 'all')
        ? ' <span class="bcp-tab__count">' + count + '</span>'
        : '';
      return '<button class="bcp-tab' + (isActive ? ' is-active' : '') + '"' +
        ' data-tab="' + t.key + '"' +
        ' role="tab"' +
        ' id="bcp-tab-btn-' + t.key + '"' +
        ' aria-selected="' + (isActive ? 'true' : 'false') + '"' +
        ' aria-controls="bcp-main-grid">' +
        esc(t.label) + countHtml +
        '</button>';
    }).join('');

    el.tabBar.querySelectorAll('[data-tab]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.activeTab = btn.dataset.tab;
        renderTabBar();
        renderTabContent();
      });
    });
  }

  // ── Tab content ────────────────────────────────────────────────────────────────
  function renderTabContent() {
    if (!el.mainGrid) return;
    renderSectionHead(state.activeTab);

    if (state.loggedIn === false && state.activeTab !== 'available') {
      el.mainGrid.setAttribute('aria-labelledby', 'bcp-tab-btn-' + state.activeTab);
      el.mainGrid.innerHTML = loggedOutHtml(cfg.loginCtaText);
      return;
    }

    el.mainGrid.setAttribute('aria-labelledby', 'bcp-tab-btn-' + state.activeTab);

    if (state.activeTab === 'available') {
      renderAvailableTab();
    } else if (state.activeTab === 'points') {
      renderPointsTab();
    } else if (state.activeTab === 'vip') {
      renderVipTab();
    } else if (state.activeTab === 'referrals') {
      renderReferralTab();
    } else if (state.activeTab === 'reviews') {
      renderReviewsTab();
    } else if (state.activeTab === 'activity') {
      renderActivityTab();
    } else if (state.activeTab === 'missions') {
      renderMissionsTab();
    } else {
      renderWalletTab(state.activeTab);
    }
  }

  function renderSectionHead(tab) {
    if (!el.sectionHead) return;
    var titles = {
      available: 'Available Coupons',
      wallet:    'My Coupon Wallet',
      expiring:  'Expiring Soon',
      used:      'Used & Expired',
      all:       'All Coupons',
      points:    'Bara Points',
      vip:       'VIP Status',
      referrals: 'Referral Rewards',
      reviews:   'Review Rewards',
      activity:  'Recent Activity',
      missions:  'Reward Missions'
    };

    var collectAllHtml = '';
    if (tab === 'available' && state.campaigns.length > 0 && state.loggedIn !== false) {
      var uncollected = state.campaigns.filter(function (c) {
        return !state.claimedIds.has(String(c.id));
      }).length;
      var allDone  = uncollected === 0;
      var btnLabel = allDone ? 'All Collected' : 'Collect All (' + uncollected + ')';
      collectAllHtml = '<button class="bcp-btn bcp-btn--collect-all" id="bcp-collect-all"' +
        (allDone ? ' disabled' : '') + '>' + esc(btnLabel) + '</button>';
    }

    el.sectionHead.innerHTML =
      '<h2>' + esc(titles[tab] || 'Coupons') + '</h2>' +
      collectAllHtml;

    var btn = document.getElementById('bcp-collect-all');
    if (btn) {
      el.collectAll = btn;
      btn.onclick = handleCollectAll;
    }
  }

  function renderAvailableTab() {
    if (!el.mainGrid) return;

    if (!state.campaigns.length) {
      // Logged-out with nothing to browse → login prompt. Any other empty state → plain message.
      el.mainGrid.innerHTML = state.loggedIn === false
        ? loggedOutHtml(cfg.loginCtaText)
        : emptyHtml('🎫', cfg.availableEmptyText);
      return;
    }

    // Campaigns are publicly browsable — render regardless of login state.
    // campaignCardHtml handles the logged-out action button variant.
    el.mainGrid.innerHTML = state.campaigns.map(campaignCardHtml).join('');
    wireCodeChips(el.mainGrid);
    el.mainGrid.querySelectorAll('[data-collect-id]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        handleCollect([btn.dataset.collectId], btn);
      });
    });
  }

  function renderWalletTab(tab) {
    if (!el.mainGrid) return;

    var coupons, emptyMsg, emptyIcon;
    if (tab === 'wallet') {
      coupons   = state.walletGroups.unused || [];
      emptyMsg  = (state.walletCounts.all || 0) === 0
        ? cfg.walletEmptyText
        : 'No unused coupons in your wallet.';
      emptyIcon = '👜';
    } else if (tab === 'expiring') {
      coupons   = state.walletGroups.expiring_soon || [];
      emptyMsg  = cfg.expiringEmptyText;
      emptyIcon = '✅';
    } else if (tab === 'used') {
      coupons   = (state.walletGroups.used || []).concat(state.walletGroups.expired || []);
      emptyMsg  = cfg.usedEmptyText;
      emptyIcon = '🎫';
    } else {
      coupons   = (state.wallet && state.wallet.coupons) ? state.wallet.coupons : [];
      emptyMsg  = cfg.walletEmptyText;
      emptyIcon = '🎫';
    }

    if (!coupons.length) {
      el.mainGrid.innerHTML = emptyHtml(emptyIcon, emptyMsg);
      return;
    }

    el.mainGrid.innerHTML = coupons.map(walletCouponCardHtml).join('');
    wireCodeChips(el.mainGrid);
  }

  // ── Rewards Center tabs ────────────────────────────────────────────────────────

  var BCP_PTS_SOURCE_LABELS = {
    daily_checkin:      'Daily Check-In',
    order:              'Purchase Reward',
    order_bonus:        'Order Bonus',
    admin_adjustment:   'Service Bonus',
    referral:           'Referral Bonus',
    referral_reward:    'Referral Bonus',
    review_reward:      'Review Reward',
    mission_reward:     'Mission Reward',
    points_redemption:  'Redeemed for Coupon',
    expire:             'Points Expired',
    reverse:            'Reversal',
  };

  function ptsSourceLabel(sourceType, direction, description) {
    if (description) return description;
    if (direction === 'expire') return 'Points Expired';
    if (direction === 'reverse') return 'Reversal';
    return BCP_PTS_SOURCE_LABELS[sourceType] || (sourceType || 'Points').replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  }

  function renderPointsHistoryTable(items) {
    if (!items || !items.length) {
      return '<div class="bcp-pts-table-empty">No transactions in this category yet.</div>';
    }
    var rows = items.map(function(item) {
      var dirClass = item.direction === 'earn' || (item.direction === 'adjustment' && item.points_delta > 0)
        ? 'bcp-pts-amt--earn'
        : (item.direction === 'redeem' ? 'bcp-pts-amt--redeem' : 'bcp-pts-amt--expire');
      var sign = (item.direction === 'earn' || (item.direction === 'adjustment' && item.points_delta > 0)) ? '+' : '-';
      var amt  = Math.abs(item.points_delta);
      var dateStr = item.created_at
        ? new Date(item.created_at).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')
        : '—';
      var expStr = item.expires_at
        ? new Date(item.expires_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        : '—';
      return '<tr>' +
        '<td class="bcp-pts-td bcp-pts-td--date">' + dateStr + '</td>' +
        '<td class="bcp-pts-td bcp-pts-td--type">' + esc(ptsSourceLabel(item.source_type, item.direction, item.description)) + '</td>' +
        '<td class="bcp-pts-td bcp-pts-td--amt"><span class="bcp-pts-amt ' + dirClass + '">' + sign + amt + '</span></td>' +
        '<td class="bcp-pts-td bcp-pts-td--exp">' + expStr + '</td>' +
      '</tr>';
    }).join('');
    return '<table class="bcp-pts-table" role="table">' +
      '<thead><tr>' +
        '<th class="bcp-pts-th">Date &amp; Time</th>' +
        '<th class="bcp-pts-th">Point Type</th>' +
        '<th class="bcp-pts-th">Points</th>' +
        '<th class="bcp-pts-th">Expiration</th>' +
      '</tr></thead>' +
      '<tbody>' + rows + '</tbody>' +
    '</table>';
  }

  function fetchPointsHistory(filter, append) {
    if (!urls.pointsHistory || state.pointsHistoryLoading) return;
    state.pointsHistoryLoading = true;
    var offset = (append && state.pointsHistory) ? (state.pointsHistory.items.length) : 0;
    var url    = urls.pointsHistory + '&filter=' + filter + '&limit=' + cfg.pointsHistoryLimit + '&offset=' + offset;
    apiFetch(url)
      .then(function(d) {
        state.pointsHistoryLoading = false;
        if (!d || !d.ok) throw new Error('history fetch error');
        if (append && state.pointsHistory) {
          state.pointsHistory.items = state.pointsHistory.items.concat(d.items || []);
          state.pointsHistory.has_more = d.has_more;
          state.pointsHistory.total    = d.total;
        } else {
          state.pointsHistory = { items: d.items || [], total: d.total || 0, has_more: d.has_more || false };
        }
        renderPointsTab();
      })
      .catch(function() {
        state.pointsHistoryLoading = false;
        var panel = el.mainGrid && el.mainGrid.querySelector('.bcp-pts-history');
        if (panel) panel.innerHTML = '<div class="bcp-pts-table-empty">Could not load history. Please try again.</div>';
      });
  }

  function renderPointsTab() {
    if (!el.mainGrid) return;
    var pts = state.pointsState;
    if (!pts) {
      el.mainGrid.innerHTML = emptyHtml('⭐', 'Points data is not available right now.');
      return;
    }
    var available  = pts.available_points || 0;
    var expiringSn = cfg.pointsShowExpiring ? (pts.expiring_soon_points || 0) : null;

    var redeemHtml = '';
    if (pts.redemption_enabled) {
      var tiers = [
        { tier: 'PTS_5',  label: '$5 off',  pts: 100, min: '$30'  },
        { tier: 'PTS_10', label: '$10 off', pts: 200, min: '$50'  },
        { tier: 'PTS_25', label: '$25 off', pts: 400, min: '$100' }
      ];
      redeemHtml =
        '<div class="bcp-rc-pts-tiers">' +
        tiers.map(function(t) {
          var canAfford = available >= t.pts;
          return '<div class="bcp-rc-pts-tier">' +
            '<div class="bcp-rc-pts-tier-info">' +
              '<span class="bcp-rc-pts-tier-label">' + esc(t.label) + '</span>' +
              '<span class="bcp-rc-pts-tier-cost">' + t.pts + ' pts</span>' +
              '<span class="bcp-rc-pts-tier-min">Min order ' + t.min + '</span>' +
            '</div>' +
            '<button type="button" class="bcp-btn bcp-btn--primary bcp-btn--sm' + (canAfford ? '' : ' is-disabled') + '"' +
              ' data-bcp-points-redeem="' + t.tier + '"' +
              (canAfford ? '' : ' disabled aria-disabled="true"') + '>' +
              (canAfford ? 'Redeem' : 'Need ' + t.pts + ' pts') +
            '</button>' +
          '</div>';
        }).join('') +
        '</div>';
    } else if (pts.redemption_test_mode) {
      redeemHtml = '<p class="bcp-rc-pts-note">Points redemption is in early testing. Keep earning — it\'s coming soon!</p>';
    } else {
      redeemHtml = '<p class="bcp-rc-pts-note">Redemption coming soon. Keep earning!</p>';
    }

    var expiringSoonHtml = expiringSn !== null
      ? '<div class="bcp-pts-stat-box">' +
          '<span class="bcp-pts-stat-big bcp-pts-stat-big--neutral">' + expiringSn + '</span>' +
          '<span class="bcp-pts-stat-lbl">Points expiring soon' +
            '<span class="bcp-pts-tooltip-wrap" aria-label="Points expiring in 30 days">' +
              '<span class="bcp-pts-tooltip-icon" tabindex="0" role="button" aria-expanded="false">ⓘ</span>' +
              '<span class="bcp-pts-tooltip-body" role="tooltip">Points that will expire within the next 30 days.</span>' +
            '</span>' +
          '</span>' +
        '</div>'
      : '';

    var historyHtml = '';
    if (cfg.pointsShowHistory) {
      var subTabs = ['all', 'earned', 'used', 'expired'];
      var subTabLabels = { all: 'All', earned: 'Earned', used: 'Used', expired: 'Expired' };
      var tabsHtml = subTabs.map(function(t) {
        var active = (state.pointsHistoryFilter === t) ? ' bcp-pts-tab--active' : '';
        return '<button type="button" class="bcp-pts-tab' + active + '" data-bcp-pts-subtab="' + t + '">' + subTabLabels[t] + '</button>';
      }).join('');

      var tableContent = '';
      if (state.pointsHistoryLoading && !state.pointsHistory) {
        tableContent = '<div class="bcp-pts-table-loading">Loading…</div>';
      } else if (state.pointsHistory) {
        tableContent = renderPointsHistoryTable(state.pointsHistory.items);
        if (state.pointsHistory.has_more) {
          tableContent += '<div class="bcp-pts-load-more"><button type="button" class="bcp-btn bcp-btn--primary bcp-btn--sm" data-bcp-pts-loadmore>Load more</button></div>';
        }
      } else {
        tableContent = '<div class="bcp-pts-table-loading">Loading…</div>';
      }

      historyHtml =
        '<div class="bcp-pts-history-section">' +
          '<div class="bcp-pts-subtabs" role="tablist" aria-label="Points history filter">' + tabsHtml + '</div>' +
          '<div class="bcp-pts-history">' + tableContent + '</div>' +
        '</div>';
    }

    el.mainGrid.innerHTML =
      '<div class="bcp-rc-panel bcp-rc-panel--pts">' +
        '<div class="bcp-pts-stats-row">' +
          '<div class="bcp-pts-stat-box">' +
            '<span class="bcp-pts-stat-big">' + available + '</span>' +
            '<span class="bcp-pts-stat-lbl">Total Points</span>' +
          '</div>' +
          (pts.pending_points > 0
            ? '<div class="bcp-pts-stat-box">' +
                '<span class="bcp-pts-stat-big bcp-pts-stat-big--pending">' + pts.pending_points + '</span>' +
                '<span class="bcp-pts-stat-lbl">Pending</span>' +
              '</div>'
            : '') +
          expiringSoonHtml +
        '</div>' +
        '<div class="bcp-pts-divider"></div>' +
        redeemHtml +
        historyHtml +
        '<p class="bcp-rc-pts-earn-note">Earn points from daily check-ins and qualifying orders.</p>' +
      '</div>';

    el.mainGrid.querySelectorAll('[data-bcp-points-redeem]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        if (btn.disabled || btn.classList.contains('is-disabled') || btn.dataset.bcpRedeemPending) return;
        var tier = btn.getAttribute('data-bcp-points-redeem');
        btn.dataset.bcpRedeemPending = '1';
        var orig = btn.textContent;
        btn.textContent = 'Redeeming…';
        btn.disabled = true;
        fetch(BCP_REDEEM_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tier: tier })
        })
        .then(function(r) { return r.json(); })
        .then(function(data) {
          if (data.ok) {
            btn.textContent = 'Saved ✓';
            btn.classList.add('is-redeemed');
            showToast('Reward saved to your wallet!', 'success');
            if (window.BBRewardNotifications) window.BBRewardNotifications.show('points_redeemed', { coupon_code: data.coupon_code || '' });
          } else {
            btn.textContent = orig;
            btn.disabled = false;
            delete btn.dataset.bcpRedeemPending;
            showToast('Could not redeem. Please try again.', 'error');
          }
        })
        .catch(function() {
          btn.textContent = orig;
          btn.disabled = false;
          delete btn.dataset.bcpRedeemPending;
          showToast('Something went wrong.', 'error');
        });
      });
    });

    if (cfg.pointsShowHistory) {
      if (!state.pointsHistory && !state.pointsHistoryLoading) {
        fetchPointsHistory(state.pointsHistoryFilter, false);
      }
      el.mainGrid.querySelectorAll('[data-bcp-pts-subtab]').forEach(function(btn) {
        btn.addEventListener('click', function() {
          var filter = btn.getAttribute('data-bcp-pts-subtab');
          if (filter === state.pointsHistoryFilter && state.pointsHistory) return;
          state.pointsHistoryFilter = filter;
          state.pointsHistory       = null;
          renderPointsTab();
          fetchPointsHistory(filter, false);
        });
      });
      var loadMoreBtn = el.mainGrid.querySelector('[data-bcp-pts-loadmore]');
      if (loadMoreBtn) {
        loadMoreBtn.addEventListener('click', function() {
          fetchPointsHistory(state.pointsHistoryFilter, true);
        });
      }
      el.mainGrid.querySelectorAll('.bcp-pts-tooltip-icon').forEach(function(icon) {
        icon.addEventListener('click', function() {
          var expanded = icon.getAttribute('aria-expanded') === 'true';
          icon.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          icon.closest('.bcp-pts-tooltip-wrap').classList.toggle('is-open', !expanded);
        });
      });
    }
  }

  var BCP_VIP_LADDER = [
    { id: 'bronze',     label: 'Bronze Bro', short: 'B',  score: 0   },
    { id: 'silver',     label: 'Silver Bro', short: 'S',  score: 10  },
    { id: 'gold',       label: 'Gold Bro',   short: 'G',  score: 25  },
    { id: 'club_vip',   label: 'Club VIP',   short: 'V',  score: 50  },
    { id: 'bara_elite', label: 'Bara Elite', short: 'E',  score: 100 }
  ];

  var BCP_VIP_PERKS = [
    { id: 'deals',    icon: '👑', label: 'Exclusive Deals',   desc: 'Early access to exclusive sale events and new drops.',                  minTier: 'bronze'     },
    { id: 'coupon',   icon: '🎟', label: 'VIP Coupon',        desc: 'Unlock coupon available to active VIP members.',                        minTier: 'bronze'     },
    { id: 'early',    icon: '🎫', label: 'Early Access',      desc: 'Private early access to new collections before they go public.',        minTier: 'silver'     },
    { id: 'support',  icon: '⚡', label: 'Priority Support',  desc: 'Faster response from the Bara Bros support team.',                      minTier: 'silver'     },
    { id: 'service',  icon: '🎧', label: 'VIP Service',       desc: 'Dedicated VIP customer service for orders and product help.',           minTier: 'gold'       },
    { id: 'birthday', icon: '🎂', label: 'Birthday Reward',   desc: 'Special coupon surprise delivered during your birthday month.',         minTier: 'gold'       },
    { id: 'guide',    icon: '📖', label: 'Style Guide',        desc: 'Curated lookbooks and Bara Bros exclusive style guides.',               minTier: 'club_vip'   },
    { id: 'elite',    icon: '✦',  label: 'Elite Promos',       desc: 'Exclusive Elite-tier discount events and private promotions.',          minTier: 'bara_elite' },
  ];

  var BCP_TIER_ORDER = ['bronze', 'silver', 'gold', 'club_vip', 'bara_elite'];

  function renderVipTab() {
    if (!el.mainGrid) return;
    var vip = state.vipState;
    if (!vip) {
      el.mainGrid.innerHTML = emptyHtml('👑', 'VIP data is not available right now.');
      return;
    }

    var currentIdx = 0;
    for (var ci = 0; ci < BCP_VIP_LADDER.length; ci++) {
      if (BCP_VIP_LADDER[ci].id === vip.tier) { currentIdx = ci; break; }
    }
    var isTopTier = currentIdx >= BCP_VIP_LADDER.length - 1;

    // ── Progress step dots ──
    var stepsHtml = '<div class="bcp-vip-steps">';
    BCP_VIP_LADDER.forEach(function(t, i) {
      var cls = i < currentIdx ? 'bcp-vip-step--done' : (i === currentIdx ? 'bcp-vip-step--current' : 'bcp-vip-step--future');
      stepsHtml += '<div class="bcp-vip-step ' + cls + '">' +
        '<div class="bcp-vip-step__dot">' + esc(t.short) + '</div>' +
        '<div class="bcp-vip-step__lbl">' + esc(t.short === 'B' ? 'Bronze' : t.short === 'S' ? 'Silver' : t.short === 'G' ? 'Gold' : t.short === 'V' ? 'VIP' : 'Elite') + '</div>' +
      '</div>';
    });
    stepsHtml += '</div>';

    // ── Hero message ──
    var heroMsg = isTopTier
      ? 'Congratulations! You\'ve achieved the highest tier!'
      : esc(String(vip.points_to_next_tier)) + ' more pts to reach ' + esc(vip.next_tier_label || '');

    // ── Expiry label ──
    var expiryLabel = cfg.vipExpiryLabel.replace('{year}', new Date().getFullYear());

    // ── Progress bar (inline hero) ──
    var progressBarHtml = (!isTopTier && vip.progress_percent != null)
      ? '<div class="bcp-vip-hero__progress-track"><div class="bcp-vip-hero__progress-fill" style="width:' + (vip.progress_percent || 0) + '%"></div></div>'
      : '';

    // ── Level requirements panel ──
    var reqHtml = '';
    if (cfg.vipShowRequirements) {
      reqHtml =
        '<div class="bcp-vip-hero__right">' +
          '<p class="bcp-vip-req-title">Level Requirements</p>' +
          '<ul class="bcp-vip-req-list">' +
            BCP_VIP_LADDER.map(function(t) {
              var isCurrent = t.id === vip.tier;
              return '<li class="bcp-vip-req-item' + (isCurrent ? ' bcp-vip-req-item--current' : '') + '">' +
                '<span class="bcp-vip-req-tier bcp-vip-tier--' + t.id.replace('_','-') + '">' + esc(t.label) + '</span>' +
                '<span class="bcp-vip-req-threshold">' + t.score + '+ pts' + (isCurrent ? ' ← YOU' : '') + '</span>' +
              '</li>';
            }).join('') +
          '</ul>' +
        '</div>';
    }

    // ── Perks grid ──
    var perksGridHtml = '';
    if (cfg.vipShowPerkGrid) {
      var currentTierIdx = BCP_TIER_ORDER.indexOf(vip.tier);
      var perksCards = BCP_VIP_PERKS.map(function(p) {
        var minIdx  = BCP_TIER_ORDER.indexOf(p.minTier);
        var unlocked = currentTierIdx >= minIdx;
        var lockCls  = unlocked ? '' : ' bcp-vip-perk--locked';
        return '<div class="bcp-vip-perk-card' + lockCls + '" data-bcp-perk-id="' + p.id + '">' +
          '<span class="bcp-vip-perk-card__icon" aria-hidden="true">' + p.icon + '</span>' +
          '<span class="bcp-vip-perk-card__label">' + esc(p.label) + '</span>' +
          '<div class="bcp-vip-perk-card__tooltip" role="tooltip">' +
            '<strong>' + esc(p.label) + '</strong><br>' + esc(p.desc) +
            (!unlocked ? '<br><em>Unlocks at ' + esc(BCP_VIP_LADDER[minIdx]?.label || p.minTier) + '</em>' : '') +
          '</div>' +
        '</div>';
      }).join('') +
      '<div class="bcp-vip-perk-card bcp-vip-perk--coming">' +
        '<span class="bcp-vip-perk-card__icon">…</span>' +
        '<span class="bcp-vip-perk-card__label">Coming Soon</span>' +
      '</div>';

      perksGridHtml =
        '<div class="bcp-vip-perks-section">' +
          '<div class="bcp-vip-perks-head">' +
            '<p class="bcp-vip-perks-title">My Rewards</p>' +
            '<a href="#bcp-tab-btn-activity" class="bcp-vip-redemption-link" data-bcp-tab-link="activity">Redemption Record →</a>' +
          '</div>' +
          '<div class="bcp-vip-perks-grid">' + perksCards + '</div>' +
        '</div>';
    }

    el.mainGrid.innerHTML =
      '<div class="bcp-rc-panel bcp-rc-panel--vip">' +
        '<div class="bcp-vip-hero">' +
          '<div class="bcp-vip-hero__left">' +
            '<div class="bcp-vip-hero__identity">' +
              '<div class="bcp-vip-hero__avatar">' + esc((vip.tier_label || 'B').charAt(0).toUpperCase()) + '</div>' +
              '<div class="bcp-vip-hero__id-info">' +
                '<div class="bcp-vip-tier-badge bcp-vip-tier--' + vip.tier.replace(/_/g,'-') + '">' + esc(vip.tier_label) + '</div>' +
                '<div class="bcp-vip-hero__expiry">' + esc(expiryLabel) + '</div>' +
              '</div>' +
            '</div>' +
            stepsHtml +
            progressBarHtml +
            '<div class="bcp-vip-hero__msg">' + heroMsg + '</div>' +
          '</div>' +
          reqHtml +
        '</div>' +
        perksGridHtml +
        '<p class="bcp-rc-vip-note">Earn VIP score from saved coupons, daily check-ins, and milestones.</p>' +
      '</div>';

    el.mainGrid.querySelectorAll('[data-bcp-tab-link]').forEach(function(link) {
      link.addEventListener('click', function(e) {
        e.preventDefault();
        var tab = link.getAttribute('data-bcp-tab-link');
        var tabBtn = document.getElementById('bcp-tab-btn-' + tab);
        if (tabBtn) tabBtn.click();
      });
    });
  }

  function renderReferralTab() {
    if (!el.mainGrid) return;
    var ref = state.referralState;
    if (!ref) {
      el.mainGrid.innerHTML = emptyHtml('🔗', 'Referral data is not available right now.');
      return;
    }
    var link = ref.referral_link || '';
    var code = ref.referral_code || '';
    var statsHtml = '';
    if (ref.total_referrals != null || ref.rewards_pending != null) {
      statsHtml =
        '<div class="bcp-rc-ref-stats">' +
          '<div class="bcp-rc-ref-stat"><span class="bcp-rc-pts-val">' + (ref.total_referrals || 0) + '</span><span class="bcp-rc-pts-lbl">Total Referrals</span></div>' +
          '<div class="bcp-rc-ref-stat"><span class="bcp-rc-pts-val">' + (ref.rewards_pending || 0) + '</span><span class="bcp-rc-pts-lbl">Rewards Pending</span></div>' +
        '</div>';
    }
    el.mainGrid.innerHTML =
      '<div class="bcp-rc-panel">' +
        '<p class="bcp-rc-ref-intro">Share Bara Bros with your crew and unlock rewards when they shop.</p>' +
        (code
          ? '<div class="bcp-rc-ref-row"><span class="bcp-rc-ref-label">Your Code</span>' +
              '<div class="bcp-rc-ref-code-wrap"><code class="bcp-rc-ref-code">' + esc(code) + '</code>' +
              '<button type="button" class="bcp-btn bcp-btn--primary bcp-btn--sm" data-bcp-copy-val="' + esc(code) + '">Copy Code</button></div></div>'
          : '') +
        (link
          ? '<div class="bcp-rc-ref-row"><span class="bcp-rc-ref-label">Referral Link</span>' +
              '<div class="bcp-rc-ref-link-wrap"><span class="bcp-rc-ref-link">' + esc(link) + '</span>' +
              '<button type="button" class="bcp-btn bcp-btn--primary bcp-btn--sm" data-bcp-copy-val="' + esc(link) + '">Copy Link</button></div></div>'
          : '') +
        statsHtml +
        '<p class="bcp-rc-ref-note">Rewards are issued after your referral\'s first qualifying order.</p>' +
      '</div>';
    el.mainGrid.querySelectorAll('[data-bcp-copy-val]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var val  = btn.getAttribute('data-bcp-copy-val');
        var orig = btn.textContent;
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(val).then(function() {
            btn.textContent = 'Copied!';
            setTimeout(function() { btn.textContent = orig; }, 2000);
          }).catch(function() {});
        }
      });
    });
  }

  function renderReviewsTab() {
    if (!el.mainGrid) return;
    var reviewTiers = [
      { pts: '+25 pts', label: 'Verified text review' },
      { pts: '+50 pts', label: 'Photo review' },
      { pts: '+75 pts', label: 'Video review' },
      { pts: '+100 pts', label: 'Approved UGC / social post' }
    ];
    el.mainGrid.innerHTML =
      '<div class="bcp-rc-panel">' +
        '<p class="bcp-rc-review-intro">Earn Bara Points for every verified review you submit.</p>' +
        '<div class="bcp-rc-review-tiers">' +
          reviewTiers.map(function(t) {
            return '<div class="bcp-rc-review-tier">' +
              '<span class="bcp-rc-review-pts">' + esc(t.pts) + '</span>' +
              '<div class="bcp-rc-review-info"><span class="bcp-rc-review-label">' + esc(t.label) + '</span></div>' +
            '</div>';
          }).join('') +
        '</div>' +
        '<p class="bcp-rc-review-note">Rewards may require review before points are added to your account.</p>' +
      '</div>';
  }

  // ── Activity tab ──────────────────────────────────────────────────────────────
  function renderActivityTab() {
    if (!el.mainGrid) return;
    var activity = state.rewardsActivity;
    var items    = (activity && Array.isArray(activity.items)) ? activity.items : [];

    var itemsHtml = items.length === 0
      ? '<div class="bcp-rc-activity-empty">' +
          '<span class="bcp-rc-activity-icon" aria-hidden="true">&#128197;</span>' +
          '<p>Your reward activity will appear here once you start earning.</p>' +
        '</div>'
      : '<div class="bcp-rc-timeline">' +
          items.map(function(item) {
            var dateStr = item.created_at
              ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : '';
            var ptsBadge = item.points_delta
              ? ' <span class="bcp-rc-timeline-pts">' + (item.points_delta > 0 ? '+' : '') + item.points_delta + ' pts</span>'
              : '';
            var statusClass = 'bcp-rc-timeline-dot--' + (item.status || 'available');
            return '<div class="bcp-rc-timeline-item">' +
              '<span class="bcp-rc-timeline-dot ' + statusClass + '" aria-hidden="true">' + esc(item.icon || '•') + '</span>' +
              '<div class="bcp-rc-timeline-body">' +
                '<div class="bcp-rc-timeline-msg">' + esc(item.message || item.label || '') + ptsBadge + '</div>' +
                (dateStr ? '<div class="bcp-rc-timeline-date">' + dateStr + '</div>' : '') +
              '</div>' +
            '</div>';
          }).join('') +
        '</div>';

    el.mainGrid.innerHTML =
      '<div class="bcp-rc-panel">' +
        itemsHtml +
        (activity && activity.has_more
          ? '<div class="bcp-rc-activity-more"><a href="/account">View full history</a></div>'
          : '') +
      '</div>';
  }

  // ── Missions tab ──────────────────────────────────────────────────────────────
  function renderMissionsTab() {
    if (!el.mainGrid) return;
    var ms = state.missionState;
    if (!ms || !ms.missions || !ms.missions.length) {
      el.mainGrid.innerHTML = emptyHtml('🎯', 'Missions are not available right now.');
      return;
    }

    var STATUS_LABEL = {
      available:      'Available',
      in_progress:    'In Progress',
      completed:      'Completed',
      reward_pending: 'Reward Ready',
      rewarded:       'Rewarded',
      disabled:       'Inactive'
    };

    var order = ['reward_pending', 'in_progress', 'available', 'completed', 'rewarded', 'disabled'];
    var sorted = ms.missions.slice().sort(function(a, b) {
      return (order.indexOf(a.status) - order.indexOf(b.status));
    });

    var cardsHtml = sorted.map(function(m) {
      var pct       = m.progress_percent || 0;
      var statusLbl = STATUS_LABEL[m.status] || m.status;
      var isDone    = m.status === 'completed' || m.status === 'reward_pending' || m.status === 'rewarded';
      var isPending = m.status === 'reward_pending';
      var isRewarded = m.status === 'rewarded';
      var isDisabled = m.status === 'disabled';

      var statusClass = 'bcp-mission-status--' + m.status.replace('_', '-');
      var cardClass   = 'bcp-mission-card' + (isDone ? ' bcp-mission-card--done' : '') + (isDisabled ? ' bcp-mission-card--disabled' : '');

      var progressHtml = !isDisabled
        ? '<div class="bcp-mission-track" role="progressbar" aria-valuenow="' + pct + '" aria-valuemin="0" aria-valuemax="100">' +
            '<div class="bcp-mission-fill" style="width:' + pct + '%"></div>' +
          '</div>' +
          '<div class="bcp-mission-progress-label">' + m.progress + ' / ' + m.goal + '</div>'
        : '';

      var rewardHtml = '';
      if (m.reward_type === 'points' && m.reward_value) {
        var rewardClass = isPending ? ' bcp-mission-reward--pending' : (isRewarded ? ' bcp-mission-reward--claimed' : '');
        rewardHtml = '<span class="bcp-mission-reward' + rewardClass + '">' +
          (isRewarded ? '✓ ' : '') + '+' + m.reward_value + ' pts' +
        '</span>';
      }

      return '<div class="' + cardClass + '">' +
        '<div class="bcp-mission-card__header">' +
          '<span class="bcp-mission-title">' + esc(m.title) + '</span>' +
          '<span class="bcp-mission-status ' + statusClass + '">' + esc(statusLbl) + '</span>' +
        '</div>' +
        '<p class="bcp-mission-desc">' + esc(m.description) + '</p>' +
        progressHtml +
        '<div class="bcp-mission-card__footer">' +
          rewardHtml +
        '</div>' +
      '</div>';
    }).join('');

    var sum = ms.summary || {};
    var summaryHtml =
      '<div class="bcp-mission-summary">' +
        '<span>' + (sum.active_count || 0) + ' active</span>' +
        '<span>' + (sum.completed_count || 0) + ' completed</span>' +
        (sum.claimable_count ? '<span class="bcp-mission-summary__claimable">' + sum.claimable_count + ' reward ready</span>' : '') +
      '</div>';

    el.mainGrid.innerHTML =
      '<div class="bcp-rc-panel">' +
        summaryHtml +
        '<div class="bcp-mission-list">' + cardsHtml + '</div>' +
      '</div>';
  }

  // ── Check-in card ──────────────────────────────────────────────────────────────
  function renderCheckinCard() {
    if (!el.checkinCard) return;

    if (state.loggedIn === false) {
      el.checkinCard.innerHTML = loggedOutHtml('Log in to claim your daily reward.');
      return;
    }

    el.checkinCard.innerHTML = [
      '<div class="bcp-checkin-card__inner">',
      '  <div class="bcp-checkin-card__icon">🎁</div>',
      '  <div class="bcp-checkin-card__body">',
      '    <p class="bcp-checkin-card__title">Daily Check-In</p>',
      '    <p class="bcp-checkin-card__sub" id="bcp-checkin-status">Check in every day to earn coupon rewards.</p>',
      '  </div>',
      '  <div class="bcp-checkin-card__actions">',
      '    <button class="bcp-btn bcp-btn--primary" id="bcp-checkin-btn">Claim Daily Reward</button>',
      '  </div>',
      '</div>'
    ].join('');

    el.checkinBtn    = document.getElementById('bcp-checkin-btn');
    el.checkinStatus = document.getElementById('bcp-checkin-status');
    el.checkinBtn.addEventListener('click', handleCheckin);
  }

  // ── Campaign grid (legacy mode) ───────────────────────────────────────────────
  function renderCampaignGrid() {
    if (!el.campaignGrid) return;

    if (state.loggedIn === false) {
      el.campaignGrid.innerHTML = loggedOutHtml('Log in to collect available coupons.');
      if (el.collectAll) el.collectAll.style.display = 'none';
      return;
    }

    if (!state.campaigns.length) {
      el.campaignGrid.innerHTML = emptyHtml('🎫', cfg.availableEmptyText || 'No coupons available right now.');
      if (el.collectAll) el.collectAll.style.display = 'none';
      return;
    }

    el.campaignGrid.innerHTML = state.campaigns.map(campaignCardHtml).join('');
    wireCodeChips(el.campaignGrid);

    el.campaignGrid.querySelectorAll('[data-collect-id]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        handleCollect([btn.dataset.collectId], btn);
      });
    });

    if (el.collectAll) {
      var uncollected = state.campaigns.filter(function (c) {
        return !state.claimedIds.has(String(c.id));
      }).length;
      el.collectAll.style.display = '';
      el.collectAll.disabled = (uncollected === 0);
      el.collectAll.textContent = uncollected > 0 ? 'Collect All (' + uncollected + ')' : 'All Collected';
      el.collectAll.onclick = handleCollectAll;
    }
  }

  /* ── Clean customer-facing coupon label (strips backend source language) ── */
  function cleanCouponDisplay(c) {
    var lblCfg       = window.BB_COUPON_LABELS_CFG || {};
    var productLabel = lblCfg.productCouponLabel  || 'Limited-Time Product Coupon';
    var refMode      = lblCfg.productRefDisplay   || 'product_name';
    var maxLen       = lblCfg.productNameMaxLength || 40;
    var showLink     = lblCfg.showProductLink      !== false;
    var linkText     = lblCfg.productLinkText      || 'View eligible product';

    var title    = String(c.title    || 'Coupon');
    var subtitle = String(c.subtitle || '');

    var isBackend = /\bscheduler\b/i.test(subtitle) || /product.targeted coupon for/i.test(subtitle);
    var isProduct = (c.applies_to && c.applies_to.type === 'products')
      || c.source === 'scheduler' || c.source === 'manual_product';

    if (isProduct && isBackend) subtitle = productLabel;

    var handle = '', pTitle = '', refText = '', linkLabel = '';
    if (isProduct) {
      var handles = (c.applies_to && c.applies_to.product_handles) || [];
      var ptitles = (c.applies_to && c.applies_to.product_titles)  || [];
      handle = String(handles[0] || c.product_handle || '');
      pTitle = String(ptitles[0] || c.product_title  || '');
      var dname = pTitle || handle;
      if      (refMode === 'product_name'          && dname)  refText = 'Eligible for ' + dname;
      else if (refMode === 'product_name_ellipsis' && dname)  refText = 'Eligible for ' + (dname.length > maxLen ? dname.slice(0, maxLen) + '…' : dname);
      else if (refMode === 'product_slug'          && handle) refText = 'Eligible product: ' + handle;
      else if (refMode === 'generic')                         refText = 'Eligible product only';
      if (showLink && handle) {
        linkLabel = linkText === 'product_name'   ? (pTitle || handle)
                  : linkText === 'product_handle' ? handle
                  : linkText;
      }
    }
    return { title: title, subtitle: subtitle, refText: refText, handle: handle, linkLabel: linkLabel };
  }

  function campaignCardHtml(c) {
    var val    = formatValue(c);
    var minOrd = c.minimum_order ? 'Min. order $' + c.minimum_order : '';
    // Primary: match campaign_id (FK). Fallback: code match for duplicate-code campaigns.
    var alreadyClaimed = state.claimedIds.has(String(c.id))
      || (c.code && state.claimedCodes && state.claimedCodes.has(String(c.code).toLowerCase()));

    var countdownHtml = '';
    var urgencyClass  = '';
    if (cfg.countdownEnabled && c.expires_at) {
      var exp = new Date(c.expires_at).getTime();
      if (!isNaN(exp)) {
        var ms = exp - Date.now();
        var expiringSoonMs = cfg.expiringSoonHours * 3600000;
        var urgency = getUrgencyLevel(ms, expiringSoonMs);
        if (urgency) urgencyClass = ' bcp-coupon-card--urgency-' + urgency;
        countdownHtml = '<span class="bcp-countdown" data-expires-at="' + esc(c.expires_at) + '" data-countdown-for="active">' + formatCountdown(ms) + '</span>';
      }
    }

    var badgeLabelHtml = c.badge_label
      ? '<span class="bcp-badge bcp-badge--available">' + esc(c.badge_label) + '</span>'
      : '';

    var actionHtml = alreadyClaimed
      ? '<span class="bcp-badge bcp-badge--saved">Saved to Wallet</span>'
      : state.loggedIn === false
        ? '<a href="' + esc(loginUrl()) + '" class="bcp-btn bcp-btn--primary bcp-btn--sm">Log In to Save</a>'
        : '<button class="bcp-btn bcp-btn--primary bcp-btn--sm" data-collect-id="' + esc(c.id) + '" data-coupon-collect data-campaign-id="' + esc(c.id) + '" data-coupon-code="' + esc(c.code || '') + '">Collect</button>';

    var codeHtml = c.code
      ? '<span class="bcp-code-chip" data-code="' + esc(c.code) + '" role="button" tabindex="0" aria-label="Copy coupon code ' + esc(c.code) + '">' + esc(c.code) + ' <small>📋</small></span>'
      : '<span></span>';

    var lbl = cleanCouponDisplay(c);
    return [
      '<div class="bcp-coupon-card' + urgencyClass + '" data-coupon-card data-campaign-id="' + esc(c.id) + '" data-coupon-code="' + esc(c.code || '') + '" data-discount-node-id="' + esc(c.discount_node_id || '') + '" data-wallet-saved="' + (alreadyClaimed ? 'true' : 'false') + '">',
      '  <div class="bcp-coupon-card__left">',
      '    <div class="bcp-coupon-card__value">'      + esc(val.main) + '</div>',
      '    <div class="bcp-coupon-card__value-unit">' + esc(val.unit) + '</div>',
      '  </div>',
      '  <div class="bcp-coupon-card__right">',
      '    <div>',
      '      <p class="bcp-coupon-card__title">' + esc(lbl.title) + '</p>',
      (lbl.subtitle  ? '      <p class="bcp-coupon-card__subtitle">' + esc(lbl.subtitle) + '</p>' : ''),
      (lbl.refText   ? '      <p class="bcp-product-ref">' + esc(lbl.refText) + '</p>' : ''),
      (lbl.linkLabel ? '      <a class="bcp-product-link" href="/products/' + esc(lbl.handle) + '">' + esc(lbl.linkLabel) + '</a>' : ''),
      '    </div>',
      '    <div class="bcp-coupon-card__meta">',
      (minOrd         ? '<span>' + esc(minOrd) + '</span>' : ''),
      countdownHtml,
      badgeLabelHtml,
      '    </div>',
      '    <div class="bcp-coupon-card__actions">',
      '      ' + codeHtml,
      '      ' + actionHtml,
      '    </div>',
      '  </div>',
      '</div>'
    ].join('');
  }

  // ── Filter chips (legacy mode) ────────────────────────────────────────────────
  function renderFilterChips() {
    if (!el.filterChips) return;
    if (state.loggedIn === false) { el.filterChips.innerHTML = ''; return; }

    var c = state.walletCounts;
    var filters = [
      { key: 'all',           label: 'All',           count: c.all           || 0 },
      { key: 'new',           label: 'New',           count: c.new           || 0 },
      { key: 'unused',        label: 'Unused',        count: c.unused        || 0 },
      { key: 'expiring_soon', label: 'Expiring Soon', count: c.expiring_soon || 0 },
      { key: 'used',          label: 'Used',          count: c.used          || 0 },
      { key: 'expired',       label: 'Expired',       count: c.expired       || 0 }
    ];

    el.filterChips.innerHTML = filters.map(function (f) {
      var active = state.activeFilter === f.key ? ' is-active' : '';
      return '<button class="bcp-chip' + active + '" data-filter="' + f.key + '">' +
        esc(f.label) +
        ' <span class="bcp-chip__count">' + f.count + '</span>' +
        '</button>';
    }).join('');

    el.filterChips.querySelectorAll('[data-filter]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.activeFilter = btn.dataset.filter;
        renderFilterChips();
        renderWalletGrid();
      });
    });
  }

  // ── Wallet grid (legacy mode) ─────────────────────────────────────────────────
  function renderWalletGrid() {
    if (!el.walletGrid) return;

    if (state.loggedIn === false) {
      el.walletGrid.innerHTML = loggedOutHtml('Log in to save and view your coupons.');
      return;
    }

    var coupons;
    if (state.activeFilter === 'all') {
      coupons = (state.wallet && state.wallet.coupons) ? state.wallet.coupons : [];
    } else {
      coupons = state.walletGroups[state.activeFilter] || [];
    }

    if (!coupons.length) {
      var emptyMsg = (state.walletCounts.all || 0) === 0
        ? cfg.walletEmptyText
        : 'No ' + state.activeFilter.replace('_', ' ') + ' coupons.';
      el.walletGrid.innerHTML = emptyHtml('🎫', emptyMsg);
      return;
    }

    el.walletGrid.innerHTML = coupons.map(walletCouponCardHtml).join('');
    wireCodeChips(el.walletGrid);
  }

  function walletCouponCardHtml(c) {
    var now            = Date.now();
    var expiresMs      = c.expires_at ? new Date(c.expires_at).getTime() : Infinity;
    var claimedAt      = c.claimed_at ? new Date(c.claimed_at).getTime() : 0;
    var expiringSoonMs = cfg.expiringSoonHours * 3600000;

    var displayStatus;
    if (c.status === 'used') {
      displayStatus = 'used';
    } else if (expiresMs !== Infinity && expiresMs <= now) {
      displayStatus = 'expired';
    } else {
      displayStatus = 'unused';
    }

    var isExpiringSoon = displayStatus === 'unused' && expiresMs !== Infinity && (expiresMs - now) <= expiringSoonMs;
    var isNew          = displayStatus === 'unused' && (now - claimedAt) <= 259200000; // 72h

    var cardClass    = displayStatus === 'used' ? ' is-used' : displayStatus === 'expired' ? ' is-expired' : '';
    var urgencyClass = '';
    if (cfg.countdownEnabled && displayStatus === 'unused' && expiresMs !== Infinity) {
      var urgency = getUrgencyLevel(expiresMs - now, expiringSoonMs);
      if (urgency) urgencyClass = ' bcp-coupon-card--urgency-' + urgency;
    }

    var badge;
    if      (displayStatus === 'used')    badge = '<span class="bcp-badge bcp-badge--used">Used</span>';
    else if (displayStatus === 'expired') badge = '<span class="bcp-badge bcp-badge--expired">Expired</span>';
    else if (isExpiringSoon)              badge = '<span class="bcp-badge bcp-badge--expiring">Expiring Soon</span>';
    else if (isNew)                       badge = '<span class="bcp-badge bcp-badge--new">New</span>';
    else                                  badge = '<span class="bcp-badge bcp-badge--unused">Ready to Use</span>';

    var val      = formatValue(c);
    var minOrd   = c.minimum_order ? 'Min. order $' + c.minimum_order : '';
    var claimedS = c.claimed_at  ? 'Claimed '  + formatDate(c.claimed_at) : '';

    var countdownHtml = '';
    if (cfg.countdownEnabled) {
      if (displayStatus === 'used') {
        countdownHtml = '<span class="bcp-countdown bcp-countdown--static">Used</span>';
      } else if (displayStatus === 'expired') {
        countdownHtml = '<span class="bcp-countdown bcp-countdown--static">Expired</span>';
      } else if (!c.expires_at || isNaN(expiresMs) || expiresMs === Infinity) {
        countdownHtml = '<span class="bcp-countdown bcp-countdown--static">No expiration</span>';
      } else {
        countdownHtml = '<span class="bcp-countdown" data-expires-at="' + esc(c.expires_at) + '" data-countdown-for="active">' + formatCountdown(expiresMs - now) + '</span>';
      }
    } else {
      var expiry = c.expires_at ? 'Expires ' + formatDate(c.expires_at) : '';
      if (expiry) countdownHtml = '<span>' + esc(expiry) + '</span>';
    }

    var codeHtml = c.code
      ? '<span class="bcp-code-chip" data-code="' + esc(c.code) + '" role="button" tabindex="0" aria-label="Copy coupon code ' + esc(c.code) + '">' + esc(c.code) + ' <small>📋</small></span>'
      : '<span></span>';

    var lbl = cleanCouponDisplay(c);
    return [
      '<div class="bcp-coupon-card' + cardClass + urgencyClass + '">',
      '  <div class="bcp-coupon-card__left">',
      '    <div class="bcp-coupon-card__value">'      + esc(val.main) + '</div>',
      '    <div class="bcp-coupon-card__value-unit">' + esc(val.unit) + '</div>',
      '  </div>',
      '  <div class="bcp-coupon-card__right">',
      '    <p class="bcp-coupon-card__title">' + esc(lbl.title) + '</p>',
      (lbl.subtitle  ? '    <p class="bcp-coupon-card__subtitle">' + esc(lbl.subtitle) + '</p>' : ''),
      (lbl.refText   ? '    <p class="bcp-product-ref">' + esc(lbl.refText) + '</p>' : ''),
      (lbl.linkLabel ? '    <a class="bcp-product-link" href="/products/' + esc(lbl.handle) + '">' + esc(lbl.linkLabel) + '</a>' : ''),
      '    <div class="bcp-coupon-card__meta">',
      (minOrd   ? '<span>' + esc(minOrd)   + '</span>' : ''),
      countdownHtml,
      (claimedS ? '<span>' + esc(claimedS) + '</span>' : ''),
      '    </div>',
      '    <div class="bcp-coupon-card__actions">',
      '      ' + codeHtml,
      '      ' + badge,
      '    </div>',
      '  </div>',
      '</div>'
    ].join('');
  }

  // ── SessionStorage cache helpers ───────────────────────────────────────────────
  function getCachedCampaigns() {
    try {
      var raw = sessionStorage.getItem(CAMPAIGNS_CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !Array.isArray(obj.campaigns)) return null;
      if (Date.now() - (obj.cachedAt || 0) > CAMPAIGNS_CACHE_TTL) return null;
      return obj.campaigns;
    } catch (e) { return null; }
  }

  function setCachedCampaigns(campaigns) {
    try {
      sessionStorage.setItem(CAMPAIGNS_CACHE_KEY, JSON.stringify({
        campaigns: campaigns,
        cachedAt:  Date.now()
      }));
    } catch (e) {}
  }

  // ── Check-in state application ─────────────────────────────────────────────────
  function applyCheckinState(checkinState) {
    if (!checkinState || !el.checkinCard) return;
    var today = new Date().toISOString().slice(0, 10);
    var btn   = document.getElementById('bcp-checkin-btn');
    if (!btn) return;
    if (checkinState.last_checkin_date === today) {
      setBtn(btn, true, 'Done for Today');
      setCheckinStatus('You already claimed today\'s reward.');
      if (checkinState.streak) showStreakBadge(checkinState.streak, checkinState.highest_streak || checkinState.streak, state.nextMilestone);
    } else {
      setBtn(btn, false, 'Claim Daily Reward');
    }
  }

  // ── Load error ─────────────────────────────────────────────────────────────────
  function showLoadError(msg) {
    var html = [
      '<div class="bcp-empty" style="grid-column:1/-1">',
      '  <span class="bcp-empty__icon">⚠</span>',
      '  <p class="bcp-empty__text">' + esc(msg) + '</p>',
      '  <button class="bcp-btn bcp-btn--outline bcp-btn--sm bcp-retry-btn" style="margin-top:14px">Refresh</button>',
      '</div>'
    ].join('');

    var target = cfg.tabsEnabled ? el.mainGrid : null;
    if (el.campaignGrid) el.campaignGrid.innerHTML = html;
    if (el.walletGrid)   el.walletGrid.innerHTML   = html;
    if (target)          target.innerHTML           = html;

    document.querySelectorAll('.bcp-retry-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var skel = '<div class="bcp-skeleton bcp-skeleton-card"></div>';
        if (el.campaignGrid) el.campaignGrid.innerHTML = skel;
        if (el.walletGrid)   el.walletGrid.innerHTML   = skel;
        if (target)          target.innerHTML           = skel;
        fetchPageData();
      });
    });
  }

  // ── Action: daily check-in ─────────────────────────────────────────────────────
  function handleCheckin() {
    var _dbg = window.location.search.indexOf('debugRewards=1') !== -1;
    var btn = document.getElementById('bcp-checkin-btn');
    if (!btn || btn.disabled) return;
    setBtn(btn, true, '…');

    if (_dbg) {
      console.log('[BB Coupons][debugRewards] === CHECK-IN CLICK ===');
      console.log('[BB Coupons][debugRewards] endpoint:', urls.checkin);
      console.log('[BB Coupons][debugRewards] method: POST | body: {}');
      console.log('[BB Coupons][debugRewards] logged_in:', state.loggedIn);
      console.log('[BB Coupons][debugRewards] window.BBCustomer:', JSON.stringify(window.BBCustomer || null));
    }

    fetch(urls.checkin, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(r) {
        var _status = r.status;
        if (_dbg) console.log('[BB Coupons][debugRewards] check-in HTTP', _status, r.ok ? 'OK' : 'ERR');
        return r.text().then(function(raw) {
          if (_dbg) console.log('[BB Coupons][debugRewards] check-in raw:', raw.slice(0, 400));
          var d;
          try { d = JSON.parse(raw); } catch(e) {
            if (_dbg) console.warn('[BB Coupons][debugRewards] check-in JSON parse error:', e.message);
            throw new Error('HTTP ' + _status + ' non-JSON');
          }
          if (_dbg) console.log('[BB Coupons][debugRewards] check-in parsed:', JSON.stringify(d).slice(0, 300));
          return d;
        });
      })
      .then(function (d) {
        if (d.login_required) {
          state.loggedIn = false;
          renderCheckinCard();
          return;
        }
        if (d.ok && d.already_checked_in) {
          var _msg = 'You already checked in today — come back tomorrow! 🎉';
          if (_dbg) console.log('[BB Coupons][debugRewards] final UI message:', _msg);
          setCheckinStatus(_msg);
          setBtn(btn, true, 'Done for Today ✓');
          return;
        }
        if (d.ok) {
          var streakNote = d.streak > 1 ? '  🔥 ' + d.streak + '-day streak!' : '';
          state.nextMilestone = d.next_milestone || null;
          var _successMsg;
          if (d.milestone_unlocked) {
            _successMsg = '🏆 ' + esc(d.milestone_unlocked.title) + ' unlocked! Check your wallet.';
            setCheckinStatus(_successMsg);
            showToast('🏆 Milestone: ' + d.milestone_unlocked.title + ' added to your wallet.', 'success');
            if (window.BBRewardNotifications) window.BBRewardNotifications.show('milestone_unlocked', { milestone: d.milestone_unlocked.title, streak: d.streak });
          } else {
            _successMsg = 'Reward saved to your coupon wallet.';
            setCheckinStatus(_successMsg);
            showToast('Reward saved to your coupon wallet!' + streakNote, 'success');
            if (window.BBRewardNotifications) window.BBRewardNotifications.show('daily_checkin', { points: d.points_awarded || '', streak: d.streak });
          }
          if (_dbg) console.log('[BB Coupons][debugRewards] final UI message:', _successMsg, '| streak:', d.streak || 0);
          if (d.streak) showStreakBadge(d.streak, d.highest_streak, d.next_milestone);
          setBtn(btn, true, '✓ Claimed');
          refreshWallet();
        } else {
          var _errMsg = d.error || d.message || 'Check-in failed. Please try again.';
          if (_dbg) console.warn('[BB Coupons][debugRewards] check-in ok:false — error:', _errMsg);
          setBtn(btn, false, 'Claim Daily Reward');
          showToast(_dbg ? _errMsg : 'Check-in failed. Please try again.', 'error');
        }
      })
      .catch(function (err) {
        if (_dbg) console.warn('[BB Coupons][debugRewards] check-in catch:', err && (err.message || err));
        setBtn(btn, false, 'Claim Daily Reward');
        showToast('Something went wrong. Check the console for details.', 'error');
      });
  }

  // ── Action: collect single coupon ──────────────────────────────────────────────
  function handleCollect(ids, triggerBtn) {
    if (triggerBtn && triggerBtn.disabled) return;
    if (triggerBtn) setBtn(triggerBtn, true, 'Saving...');

    var _dbg = window.location.search.indexOf('debugRewards=1') !== -1;
    if (_dbg) {
      console.log('[BB Coupons][debugRewards] collect click — ids:', ids);
      console.log('[BB Coupons][debugRewards] collect endpoint:', urls.claim);
      console.log('[BB Coupons][debugRewards] wallet count before:', state.walletCounts.unused || 0);
      console.log('[BB Coupons][debugRewards] saved key (campaign_id):', ids[0]);
    }

    fetch(urls.claim, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campaign_ids: ids, source: 'coupon_page' })
    })
      .then(function(r) {
        if (_dbg) console.log('[BB Coupons][debugRewards] collect HTTP status:', r.status);
        return r.text().then(function(text) {
          if (_dbg) console.log('[BB Coupons][debugRewards] collect raw response:', text.slice(0, 600));
          try { return JSON.parse(text); } catch(e) { return { ok: false, _parse_error: true }; }
        });
      })
      .then(function (d) {
        if (_dbg) console.log('[BB Coupons][debugRewards] collect parsed:', JSON.stringify(d));
        if (d.login_required) {
          window.location.href = loginUrl() + '?return_url=' + encodeURIComponent(window.location.pathname + window.location.search);
          return;
        }
        if (d.ok) {
          var n = d.claimed_count || 0;
          showToast(
            n > 0 ? (n + ' coupon' + (n > 1 ? 's' : '') + ' saved to your wallet!') : 'Already in your wallet.',
            n > 0 ? 'success' : 'default'
          );
          if (n > 0 && window.BBRewardNotifications) window.BBRewardNotifications.show('coupon_claimed', {});

          /* Optimistic UI: immediately mark all collected campaign IDs as saved
             so the card flips to "Saved to Wallet" without waiting for metafield
             propagation on the next refreshWallet() server round-trip. */
          ids.forEach(function(id) {
            state.claimedIds.add(String(id));
            /* Also add by code so duplicate-code cards all flip */
            var grid = el.mainGrid || el.campaignGrid;
            if (grid) {
              var collectBtn = grid.querySelector('[data-collect-id="' + id + '"]');
              if (collectBtn) {
                if (_dbg) console.log('[BB Coupons][debugRewards] UI state changed: flipping card for campaign', id);
                var card = collectBtn.closest('[data-coupon-card]') || collectBtn.closest('.bcp-coupon-card');
                if (card) {
                  card.setAttribute('data-wallet-saved', 'true');
                  var code = card.getAttribute('data-coupon-code') || '';
                  if (code) state.claimedCodes.add(code.toLowerCase());
                  var actions = card.querySelector('.bcp-coupon-card__actions');
                  if (actions) {
                    var codeChip = actions.querySelector('.bcp-code-chip');
                    var codeHtml = codeChip ? codeChip.outerHTML : '';
                    actions.innerHTML = codeHtml + '<span class="bcp-badge bcp-badge--saved">Saved to Wallet</span>';
                  }
                } else if (collectBtn) {
                  /* Fallback: just swap the button if card wrapper not found */
                  var parent = collectBtn.parentNode;
                  if (parent) {
                    var fallbackChip = parent.querySelector('.bcp-code-chip');
                    var fallbackHtml = fallbackChip ? fallbackChip.outerHTML : '';
                    parent.innerHTML = fallbackHtml + '<span class="bcp-badge bcp-badge--saved">Saved to Wallet</span>';
                  }
                }
              }
            }
          });

          /* Update Collect All button count and tab bar immediately */
          renderSectionHead(state.activeTab);
          if (cfg.tabsEnabled) renderTabBar();
          else if (el.collectAll) {
            var uncollected = state.campaigns.filter(function(c2) { return !state.claimedIds.has(String(c2.id)); }).length;
            el.collectAll.disabled = (uncollected === 0);
            el.collectAll.textContent = uncollected > 0 ? 'Collect All (' + uncollected + ')' : 'All Collected';
          }

          /* Clear session cache so next page load re-fetches fresh data */
          try { sessionStorage.removeItem(CAMPAIGNS_CACHE_KEY); } catch(e2) {}

          /* Background sync: refresh wallet to get server-confirmed state */
          refreshWallet();

          if (n > 0) {
            if (_dbg) console.log('[BB Coupons][debugRewards] dispatching bb:coupon:changed + bb:wallet:changed');
            try { document.dispatchEvent(new CustomEvent('bb:coupon:changed', { detail: { source: 'coupon_collect', count: n } })); } catch(e3) {}
            try {
              localStorage.setItem('bb_wallet_updated_at', String(Date.now()));
              document.dispatchEvent(new CustomEvent('bb:wallet:changed', { detail: { counts: state.walletCounts } }));
            } catch(e4) {}
          }
          if (_dbg) console.log('[BB Coupons][debugRewards] wallet count after (optimistic):', (state.walletCounts.unused || 0) + n);
        } else {
          if (triggerBtn) setBtn(triggerBtn, false, 'Collect');
          if (_dbg) console.warn('[BB Coupons][debugRewards] collect failed:', d.error || d.message || 'unknown');
          showToast('Could not collect coupon.', 'error');
        }
      })
      .catch(function (err) {
        if (triggerBtn) setBtn(triggerBtn, false, 'Collect');
        if (_dbg) console.warn('[BB Coupons][debugRewards] collect catch:', err && (err.message || err));
        showToast('Something went wrong.', 'error');
      });
  }

  // ── Action: collect all ────────────────────────────────────────────────────────
  function handleCollectAll() {
    var btn = el.collectAll;
    if (!btn || btn.disabled) return;

    var ids = state.campaigns
      .filter(function (c) { return !state.claimedIds.has(String(c.id)); })
      .map(function (c) { return String(c.id); });
    if (!ids.length) return;

    setBtn(btn, true, '…');

    apiFetch(urls.claim, {
      method: 'POST',
      body:   JSON.stringify({ campaign_ids: ids, source: 'coupon_page_collect_all' })
    })
      .then(function (d) {
        if (d.login_required) {
          window.location.href = loginUrl() + '?return_url=' + encodeURIComponent(window.location.pathname + window.location.search);
          return;
        }
        if (d.ok) {
          var n = d.claimed_count || 0;
          showToast(
            n > 0 ? (n + ' coupon' + (n > 1 ? 's' : '') + ' saved to your wallet!') : 'All coupons already in your wallet.',
            n > 0 ? 'success' : 'default'
          );
          if (n > 0 && window.BBRewardNotifications) window.BBRewardNotifications.show('coupon_claimed', {});
          if (!cfg.tabsEnabled) setBtn(btn, false, 'Collect All');
          refreshWallet();
          if (n > 0) try { document.dispatchEvent(new CustomEvent('bb:coupon:changed', { detail: { source: 'coupon_collect_all', count: n } })); } catch(e) {}
        } else {
          setBtn(btn, false, 'Collect All');
          showToast('Could not collect coupons.', 'error');
        }
      })
      .catch(function () {
        setBtn(btn, false, 'Collect All');
        showToast('Something went wrong.', 'error');
      });
  }

  // ── Code copy ──────────────────────────────────────────────────────────────────
  function wireCodeChips(container) {
    container.querySelectorAll('.bcp-code-chip[data-code]').forEach(function (chip) {
      chip.addEventListener('click', function () { copyCode(chip.dataset.code, chip); });
      chip.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copyCode(chip.dataset.code, chip); }
      });
    });
  }

  function copyCode(code, chip) {
    if (!code) return;
    if (!navigator.clipboard) {
      showToast('Copy not supported in this browser.', 'error');
      return;
    }
    navigator.clipboard.writeText(code).then(function () {
      var orig = chip.innerHTML;
      chip.classList.add('bcp-code-chip--copied');
      chip.innerHTML = esc(code) + ' ✓';
      showToast('Coupon code copied. Paste it at checkout.', 'success');
      setTimeout(function () {
        chip.innerHTML = orig;
        chip.classList.remove('bcp-code-chip--copied');
      }, 1800);
    }).catch(function () {
      showToast('Could not copy code.', 'error');
    });
  }

  // ── Wallet claimed-set builder ────────────────────────────────────────────────
  // Match by campaign_id (the FK back to campaigns) — fall back to coupon.id
  // which some older records may use as the primary key.
  function buildClaimedSets(walletCoupons) {
    var coupons = walletCoupons || [];
    state.claimedIds  = new Set(coupons.map(function(c) { return String(c.campaign_id || c.id); }));
    state.claimedCodes = new Set(
      coupons
        .filter(function(c) { return c.status !== 'expired'; })
        .map(function(c) { return String(c.code || '').toLowerCase(); })
        .filter(Boolean)
    );
  }

  // ── Campaign deduplication ────────────────────────────────────────────────────
  // Dedupe Available tab by campaign id — prevents double-render when
  // the same campaign appears twice in server data (e.g. stale cache merge).
  function dedupeCampaigns(campaigns) {
    var seen = new Set();
    return (campaigns || []).filter(function(c) {
      var key = String(c.id || c.code || c.title || '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────────
  function apiFetch(url, opts) {
    return fetch(url, Object.assign({ credentials: 'same-origin', headers: { 'Content-Type': 'application/json' } }, opts || {}))
      .then(function (r) { return r.json(); });
  }

  function setBtn(btn, disabled, text) {
    if (!btn) return;
    btn.disabled = disabled;
    if (text !== undefined) btn.textContent = text;
  }

  function setCheckinStatus(msg) {
    var el2 = document.getElementById('bcp-checkin-status');
    if (el2) el2.textContent = msg;
  }

  function showStreakBadge(streak, highest, nextMilestone) {
    var body = el.checkinCard && el.checkinCard.querySelector('.bcp-checkin-card__body');
    if (!body) return;
    var existing = body.querySelector('.bcp-streak-badge');
    if (existing) existing.remove();
    var existing2 = body.querySelector('.bcp-milestone-progress');
    if (existing2) existing2.remove();
    var badge = document.createElement('div');
    badge.className = 'bcp-streak-badge';
    var bestNote = (highest && highest > streak) ? '  (best: ' + highest + ')' : (highest && highest === streak && streak > 1 ? '  🏆 Personal best!' : '');
    badge.textContent = '🔥 ' + streak + '-day streak' + bestNote;
    body.appendChild(badge);
    if (nextMilestone && nextMilestone.days > streak) {
      var pct = Math.min(100, Math.round((streak / nextMilestone.days) * 100));
      var prog = document.createElement('div');
      prog.className = 'bcp-milestone-progress';
      prog.innerHTML =
        '<div class="bcp-milestone-progress__label">' +
          '<span>Next: ' + esc(nextMilestone.title) + '</span>' +
          '<span>' + streak + '/' + nextMilestone.days + ' days</span>' +
        '</div>' +
        '<div class="bcp-milestone-progress__track">' +
          '<div class="bcp-milestone-progress__fill" style="width:' + pct + '%"></div>' +
        '</div>';
      body.appendChild(prog);
    }
  }

  function formatCouponDiscountBadge(c) {
    if (!c) return { main: 'Deal', unit: '' };
    var type  = c.type || c.discount_type || '';
    var raw   = c.value != null ? c.value : c.discount_value;
    var value = parseFloat(raw);
    if (type === 'percentage') {
      var pct = (!isNaN(value) && value % 1 === 0) ? Math.round(value) : (isNaN(value) ? 0 : parseFloat(value.toFixed(1)));
      return { main: pct + '%', unit: 'OFF' };
    }
    if (type === 'fixed' || type === 'fixed_amount') {
      if (isNaN(value)) return { main: 'Deal', unit: '' };
      var dollars = value % 1 === 0 ? '$' + Math.round(value) : '$' + value.toFixed(2);
      return { main: dollars, unit: 'OFF' };
    }
    // Unknown type — if value is numeric format as fixed, else show fallback
    if (!isNaN(value) && value > 0) {
      return { main: '$' + (value % 1 === 0 ? Math.round(value) : value.toFixed(2)), unit: 'OFF' };
    }
    return { main: 'Deal', unit: '' };
  }
  function formatValue(c) { return formatCouponDiscountBadge(c); }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) { return iso; }
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function loginUrl() {
    return (root && root.dataset.loginUrl) || '/account/login';
  }

  function loggedOutHtml(msg) {
    return [
      '<div class="bcp-loggedout">',
      '  <div class="bcp-loggedout__icon">🔒</div>',
      '  <p>' + esc(msg) + '</p>',
      '  <a href="' + esc(loginUrl()) + '" class="bcp-btn bcp-btn--primary bcp-btn--sm">Log In</a>',
      '</div>'
    ].join('');
  }

  function emptyHtml(icon, msg) {
    return [
      '<div class="bcp-empty">',
      '  <span class="bcp-empty__icon">' + (icon || '🎫') + '</span>',
      '  <p class="bcp-empty__text">' + esc(msg || '') + '</p>',
      '</div>'
    ].join('');
  }

  // ── Toast ──────────────────────────────────────────────────────────────────────
  var toastEl, toastTimer;

  function injectToast() {
    toastEl = document.createElement('div');
    toastEl.className = 'bcp-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
  }

  function showToast(msg, type) {
    if (!toastEl) return;
    clearTimeout(toastTimer);
    toastEl.textContent = msg;
    toastEl.className = 'bcp-toast is-visible' + (type && type !== 'default' ? ' is-' + type : '');
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 3400);
  }

  // ── Countdown timers ───────────────────────────────────────────────────────────
  function formatCountdown(ms) {
    if (ms <= 0) return 'Expired';
    var totalSecs = Math.floor(ms / 1000);
    var days  = Math.floor(totalSecs / 86400);
    var hours = Math.floor((totalSecs % 86400) / 3600);
    var mins  = Math.floor((totalSecs % 3600) / 60);
    if (days > 0)  return 'Ends in ' + days + 'd ' + hours + 'h';
    if (hours > 0) return 'Ends in ' + hours + 'h ' + mins + 'm';
    return 'Ends in ' + mins + 'm';
  }

  function getUrgencyLevel(ms, expiringSoonMs) {
    if (ms <= 0 || ms > expiringSoonMs) return null;
    if (ms < 4 * 3600000)  return 'high';
    if (ms < 24 * 3600000) return 'medium';
    return 'low';
  }

  function tickCountdowns() {
    var now = Date.now();
    var expiringSoonMs = cfg.expiringSoonHours * 3600000;
    document.querySelectorAll('.bcp-countdown[data-countdown-for="active"]').forEach(function (span) {
      var exp = new Date(span.dataset.expiresAt).getTime();
      if (isNaN(exp)) return;
      var ms = exp - now;
      span.textContent = formatCountdown(ms);
      var card = span.closest && span.closest('.bcp-coupon-card');
      if (card) {
        card.classList.remove('bcp-coupon-card--urgency-low', 'bcp-coupon-card--urgency-medium', 'bcp-coupon-card--urgency-high');
        var urgency = getUrgencyLevel(ms, expiringSoonMs);
        if (urgency) card.classList.add('bcp-coupon-card--urgency-' + urgency);
      }
    });
  }

  function startCountdownTimer() {
    if (!cfg.countdownEnabled) return;
    stopCountdownTimer();
    tickCountdowns();
    countdownTimerInterval = setInterval(tickCountdowns, cfg.countdownRefreshSecs * 1000);
  }

  function stopCountdownTimer() {
    if (countdownTimerInterval) {
      clearInterval(countdownTimerInterval);
      countdownTimerInterval = null;
    }
  }

  // ── Expiring reminder ──────────────────────────────────────────────────────────
  function injectReminderComponent() {
    if (reminderEl || !cfg.reminderEnabled) return;
    reminderEl = document.createElement('div');
    reminderEl.className = 'bcp-expiring-reminder bcp-glass';
    reminderEl.style.display = 'none';
    reminderEl.innerHTML = [
      '<button class="bcp-reminder__dismiss" aria-label="Dismiss">&#215;</button>',
      '<div class="bcp-reminder__body">',
      '  <span class="bcp-reminder__icon" aria-hidden="true">&#9200;</span>',
      '  <p class="bcp-reminder__text"></p>',
      '</div>',
      '<button class="bcp-btn bcp-btn--primary bcp-btn--sm bcp-reminder__cta">View Coupons</button>'
    ].join('');
    document.body.appendChild(reminderEl);

    reminderEl.querySelector('.bcp-reminder__dismiss').addEventListener('click', dismissReminder);
    reminderEl.querySelector('.bcp-reminder__cta').addEventListener('click', function () {
      if (cfg.tabsEnabled) {
        state.activeTab = 'expiring';
        renderTabBar();
        renderTabContent();
        if (el.tabBar) el.tabBar.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        if (el.walletGrid) el.walletGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        state.activeFilter = 'expiring_soon';
        renderFilterChips();
        renderWalletGrid();
      }
    });
  }

  function isReminderDismissed() {
    try {
      var ts = parseInt(localStorage.getItem('bb_coupons_expiring_reminder_dismissed') || '0', 10);
      return ts > 0 && (Date.now() - ts) < (cfg.reminderDismissHours * 3600000);
    } catch (e) { return false; }
  }

  function dismissReminder() {
    try { localStorage.setItem('bb_coupons_expiring_reminder_dismissed', String(Date.now())); } catch (e) {}
    if (reminderEl) {
      reminderEl.classList.remove('is-visible');
      setTimeout(function () { if (reminderEl) reminderEl.style.display = 'none'; }, 380);
    }
  }

  function updateReminderComponent() {
    if (!reminderEl || !cfg.reminderEnabled) return;
    if (state.loggedIn === false || !state.wallet) {
      reminderEl.style.display = 'none';
      return;
    }
    if (isReminderDismissed()) {
      reminderEl.style.display = 'none';
      return;
    }

    var expiringSoonMs = cfg.expiringSoonHours * 3600000;
    var now = Date.now();
    var coupons = (state.wallet && state.wallet.coupons) ? state.wallet.coupons : [];
    var count = coupons.filter(function (c) {
      if (c.status === 'used') return false;
      if (!c.expires_at) return false;
      var exp = new Date(c.expires_at).getTime();
      return !isNaN(exp) && exp > now && (exp - now) <= expiringSoonMs;
    }).length;

    if (count === 0) {
      reminderEl.classList.remove('is-visible');
      setTimeout(function () { if (reminderEl) reminderEl.style.display = 'none'; }, 380);
      return;
    }

    var msgEl = reminderEl.querySelector('.bcp-reminder__text');
    if (msgEl) msgEl.textContent = 'You have ' + count + ' coupon' + (count > 1 ? 's' : '') + ' expiring soon. Use them before they disappear.';

    reminderEl.style.display = '';
    requestAnimationFrame(function () {
      if (reminderEl) reminderEl.classList.add('is-visible');
    });
  }

  // ── Reward notification helpers ───────────────────────────────────────────────
  function notifyExpiringOnce() {
    if (!window.BBRewardNotifications) return;
    if (!state.loggedIn) return;
    var coupons = (state.wallet && state.wallet.coupons) ? state.wallet.coupons : [];
    var now = Date.now();
    var expiringSoon = coupons.filter(function (c) {
      if (c.status === 'used') return false;
      var exp = new Date(c.expires_at || c.end_date || '').getTime();
      return !isNaN(exp) && exp > now && (exp - now) < 7 * 24 * 60 * 60 * 1000;
    });
    if (expiringSoon.length > 0) {
      window.BBRewardNotifications.show('coupon_expiring', { expires_in: expiringSoon.length + ' soon' });
    }
  }

  // ── Boot ───────────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      try { init(); } catch (e) { console.warn('[BB Coupons] init failed:', e); }
    });
  } else {
    try { init(); } catch (e) { console.warn('[BB Coupons] init failed:', e); }
  }

})();
